import { AppService } from '@App';
import { ActionsDTO, KeyVaultDTO, MapKeysDTO, RulesDTO } from '@DTOs';
import { AccountStatusEnum, FeatureToggle } from '@Enums';
import { Account, Client, UWResponse } from '@Models';
import { AccountDataService, ConfirmationDialogService, FeatureManagerService, GlobalService, MapKeyService, RoutesEnum, RoutingService, } from '@Services';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatTabGroup } from '@angular/material/tabs';
import { ActivatedRoute, ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { AccountMapKeysComponent } from 'app/account/account-config/account-mapkeys/account-mapkeys-list.component';
import { CanNavigateAwayWithConfirmGuardAccountConfig } from "app/new-business-workbench/guards/canNavigateAway.guard";
import $ from 'jquery';
import { forkJoin } from 'rxjs';
import { AccountInterviewComponent } from './account-interview/account-interview.component';
import { SharedModule } from '@Shared/shared.module';
import { AccountSummaryComponent } from './account-summary/account-summary.component';
import { AccountRulesComponent } from './account-rules/account-rules.component';
import { AccountIntegrationComponent } from './account-integrations/account-integration-view.component';
import { AccountIntegrationsComponent } from './account-integrations/account-integrations-list.component';
import { AccountMIBComponent } from './account-mib/account-mib.component';
import { AccountRxComponent } from './account-rx/account-rx.component';
import { AccountUnderwritingComponent } from './account-underwriting/account-underwriting.component';
import { AccountDocumentsComponent } from './account-documents/account-documents.component';
import { EventsComponent } from './account-events/events.component';
import { AccountBillingComponent } from './account-billing/account-billing.component';
import { AccountSettingsModule } from './account-settings';
import { AccountConfigStateService } from '@Services/account-config-state.service';

@Component({
    selector: 'account-config',
    host: { 'class': 'content' },
    templateUrl: './account-config.component.html',
    styleUrls: ['./account-config.component.scss'],
    imports: [SharedModule, AccountSummaryComponent, AccountInterviewComponent, AccountRulesComponent, AccountIntegrationsComponent, AccountMIBComponent, AccountRxComponent, AccountUnderwritingComponent, AccountDocumentsComponent, AccountMapKeysComponent, EventsComponent, AccountBillingComponent, AccountSettingsModule]
})
export class AccountConfigComponent implements OnInit, OnDestroy, CanNavigateAwayWithConfirmGuardAccountConfig {
    @ViewChild(AccountMapKeysComponent) accountMapKeysComponent: AccountMapKeysComponent;
    @ViewChild(MatTabGroup) tabGroup: MatTabGroup;
    @ViewChild(AccountMapKeysComponent) accountMapKeys: AccountMapKeysComponent;
    @ViewChild(AccountInterviewComponent) accountInterviewComponent: AccountInterviewComponent;
    client: Client;
    account: Account;
    accountInactive = false;

    isNewAccount = false;
    hiddenIntegrations: any = ['00000000-0000-0000-0000-000000000082'];
    menu: any = [];
    activeMenu: any = '';
    newClient = false;
    newClientForm: UntypedFormGroup;
    currentClientID: any;
    public companyLogo: any = this.appService.getDefaultLogo();

    selectedTabIndex: number;

    mapKeys: MapKeysDTO[];
    constantListMapKeys: any[];
    nonListNonVirtualMapKeys: any[];
    nonListMapKeys: any[];
    nonVirtualMapKeys: any[];
    nonConstantMapKeys: any[];
    variableMapKeys: any[];
    rules: RulesDTO[];
    showMibRisk = false;
    showRxRisk = false;
    healthQuestions: any[] = [];
    secretKeys: KeyVaultDTO[];
    activatedTabs: any = [];
    actions: ActionsDTO[] = [];
    isAdmin: boolean;
    reload = false;
    loadingData = false;
    disableStartInterview = false;
    
    // Deep linking state
    targetSettingType: string;
    targetSubTab: string;
    targetTabName: string;

    enableDeepLinking: boolean;
    
    constructor(
        public routingService: RoutingService,
        private appService: AppService,
        private route: ActivatedRoute,
        private readonly router: Router,
        private mapKeyService: MapKeyService,
        private globalService: GlobalService,
        private accountDataService: AccountDataService,
        private featureManagerService: FeatureManagerService,
        public confirmationService: ConfirmationDialogService,
        private readonly stateService: AccountConfigStateService
    ) {
    }

    ngOnInit() {
        const featureToggle = this.featureManagerService.getByName(FeatureToggle.GlobalRoutingDeepLinking);
        this.enableDeepLinking = featureToggle?.enabled ?? false;
        
        this.isAdmin = this.appService.getUserRoleName() === 'Admin';

        this.route.data.subscribe(async data => {
            this.account = data.account;

            this.accountDataService.activeAccount = this.account;
            this.client = data.client;
            this.isNewAccount = data.new;
        });
        
        if (this.enableDeepLinking) {
            this.route.params.subscribe(params => {
                this.targetSettingType = params['settingType'];
                this.targetSubTab = params['subTab'];
                this.targetTabName = params['tabName'];
            });
        }
        this.checkACL('Accounts', 'R', RoutesEnum.dashboard);

        this.newClientForm = new UntypedFormGroup({
            name: new UntypedFormControl('', [Validators.required]),
            code: new UntypedFormControl(''),
            status: new UntypedFormControl(''),
            logo: new UntypedFormControl('')
        });

        if (!this.isNewAccount) {
            this.getAccountData();
        } else {
            this.selectedTabIndex = 0;
        }

        this.accountInactive = (this.account?.statusId === AccountStatusEnum.Inactive);
        
        // Restore previous state if no deep linking is specified
        this.restoreStateIfNeeded();
        
        // Ensure sidenav stays hidden after all initialization and deep linking
        setTimeout(() => {
            $('#sidenav').addClass('hidenav');
        }, 500);
    }
        
    private getTabIndexByName(tabName: string): number {
        let currentIndex = 0;
        
        // Map tab names and calculate their indices
        const tabMapping = {
            'account': () => this.checkACL('Accounts', 'R'),
            'application': () => !this.isNewAccount && this.checkACL('Accounts', 'R'),
            'rules': () => !this.isNewAccount && this.checkACL('Accounts', 'R'), 
            'integrations': () => !this.isNewAccount,
            'mib-risk-map': () => !this.isNewAccount && this.showMibRisk,
            'rx-risk-map': () => !this.isNewAccount && this.showRxRisk,
            'underwriting': () => !this.isNewAccount,
            'documents': () => !this.isNewAccount,
            'mapkeys': () => !this.isNewAccount && this.checkACL('Accounts', 'R'),
            'events': () => !this.isNewAccount && this.checkACL('Accounts', 'R'),
            'billing': () => !this.isNewAccount && this.checkACL('Billing', 'R'),
            'settings': () => !this.isNewAccount && this.checkACL('System Data', 'R')
        };
        
        for (const [key, condition] of Object.entries(tabMapping)) {
            if (condition()) {
                if (key === tabName) {
                    return currentIndex;
                }
                currentIndex++;
            }
        }
        
        return -1; // Tab not found
    }
    
    private getTabLabelByName(tabName: string): string {
        const labelMapping = {
            'account': 'Account',
            'application': 'Application', 
            'rules': 'Rules',
            'integrations': 'Integrations',
            'mib-risk-map': 'MIB Risk Map',
            'rx-risk-map': 'Rx Risk Map', 
            'underwriting': 'Underwriting',
            'documents': 'Documents',
            'mapkeys': 'MapKeys',
            'events': 'Events',
            'billing': 'Billing',
            'settings': 'Settings'
        };
        
        return labelMapping[tabName] || tabName;
    }
    
    activateSettingsTab() {
        const settingsTabIndex = this.calculateSettingsTabIndex();
        
        if (settingsTabIndex >= 0) {
            this.setTabSelection(settingsTabIndex);
            this.activatedTabs['Settings'] = true;
        } else {
            this.selectedTabIndex = 0; // Fall back to first tab
        }    
    }
    
    private calculateSettingsTabIndex(): number {
        const tabConditions = [
            () => this.checkACL('Accounts', 'R'), // Account tab
            () => !this.isNewAccount && this.checkACL('Accounts', 'R'), // Application tab
            () => !this.isNewAccount && this.checkACL('Accounts', 'R'), // Rules tab
            () => !this.isNewAccount, // Integrations tab
            () => !this.isNewAccount && this.showMibRisk, // MIB Risk Map tab
            () => !this.isNewAccount && this.showRxRisk, // Rx Risk Map tab
            () => !this.isNewAccount, // Underwriting tab
            () => !this.isNewAccount, // Documents tab
            () => !this.isNewAccount && this.checkACL('Accounts', 'R'), // MapKeys tab
            () => !this.isNewAccount && this.checkACL('Accounts', 'R'), // Events tab
            () => !this.isNewAccount && this.checkACL('Billing', 'R'), // Billing tab
            () => !this.isNewAccount && this.checkACL('System Data', 'R') // Settings tab
        ];
        
        let currentIndex = 0;
        for (let i = 0; i < tabConditions.length; i++) {
            if (tabConditions[i]()) {
                if (i === tabConditions.length - 1) {
                    return currentIndex; // Settings tab found
                }
                currentIndex++;
            }
        }
        
        return -1; // Settings tab not available
    }
    
    private setTabSelection(index: number) {
        this.selectedTabIndex = index;
        if (this.tabGroup) {
            this.tabGroup.selectedIndex = index;
        }
    }
    
    private restoreStateIfNeeded() {
        // Only restore state if we're not deep linking
        if (!this.targetSettingType && this.account && this.client) {
            const savedState = this.stateService.getState(this.client.code, this.account.code, this.account.versionNumber);
            if (savedState) {
                // Restore tab selection
                if (savedState.selectedTabIndex !== undefined) {
                    this.selectedTabIndex = savedState.selectedTabIndex;
                    setTimeout(() => {
                        if (this.tabGroup) {
                            this.tabGroup.selectedIndex = savedState.selectedTabIndex;
                        }
                    }, 100);
                }
            }
        }
    }
    
    private saveCurrentState() {
        if (this.account && this.client) {
            this.stateService.updateState(this.client.code, this.account.code, this.account.versionNumber, {
                selectedTabIndex: this.selectedTabIndex,
                selectedSetting: this.targetSettingType,
                selectedSubTab: this.targetSubTab,
                selectedMainTab: this.targetTabName
            });
        }
    }
    
    private updateUrlForTab(tabLabel: string) {
        if (!this.enableDeepLinking || (!this.account || !this.client)) return;
        
        const tabName = this.getTabNameFromLabel(tabLabel);
        if (tabName) {
            // Navigate to the tab URL without reloading the component
            this.router.navigate([
                '/client', this.client.code, 
                'account', this.account.code, this.account.versionNumber,
                'configuration', tabName
            ], { replaceUrl: true });
        }
    }
    
    private getTabNameFromLabel(tabLabel: string): string {
        const labelToNameMapping = {
            'Account': 'account',
            'Application': 'application',
            'Rules': 'rules', 
            'Integrations': 'integrations',
            'MIB Risk Map': 'mib-risk-map',
            'Rx Risk Map': 'rx-risk-map',
            'Underwriting': 'underwriting',
            'Documents': 'documents',
            'MapKeys': 'mapkeys',
            'Events': 'events',
            'Billing': 'billing',
            'Settings': 'settings'
        };
        
        return labelToNameMapping[tabLabel] || null;
    }

    VerifyMibRxActions() {
        this.appService.getData(`Accounts/${this.account.id}/VerifyMibRxAction`).subscribe(
            data => {
                if (data.status === 'success') {

                    this.showMibRisk = (data.data.isMibAction ? true : false);
                    this.showRxRisk = (data.data.isRxAction ? true : false);

                } else {
                    this.appService.showMsg('error', data.message);
                }
            }
        );
    }

    getHealthQuestions() {
        this.appService.getData(`Questions/${this.account.workflowId}/HealthQuestions`).subscribe(
            data => {
                if (data.status === 'success') {
                    this.healthQuestions = data.data;
                }
            }
        );
    }

    canDeactivate(component: AccountConfigComponent, routerSnapshot: ActivatedRouteSnapshot, currentState: RouterStateSnapshot, nextState?: RouterStateSnapshot) {
        if (this.accountMapKeysComponent) {
            return this.accountMapKeysComponent.canDeactivate(routerSnapshot, currentState, nextState);
        } else {
            //Leave
            return true;
        }
    }

    getAccountData() {
        this.loadingData = this.reload ? false : true;
        this.getSecretKeys();
        forkJoin([
            this.mapKeyService.getMapKeys(this.account.id, false),
            this.appService.getData<UWResponse<RulesDTO[]>>(`Rules/${this.account.id}`),
            this.appService.getData(`Accounts/${this.account.id}/VerifyMibRxAction`),
            this.appService.getData(`Questions/${this.account.workflowId}/HealthQuestions`),
            this.appService.getData(`Actions/client/${this.client.id}`),
            this.appService.getAllDynamicConfigData(this.account.id)
        ]).subscribe(
            result => {
                this.setMapKeyData();

                this.rules = result[1].data;
                this.mapKeyService.rules = this.rules;

                if (result[3].status === 'success') {

                    this.showMibRisk = (result[2].data.isMibAction ? true : false);
                    this.showRxRisk = (result[2].data.isRxAction ? true : false);

                } else {
                    this.appService.showMsg('error', result[2].message);
                }

                if (result[3].status === 'success') {
                    this.healthQuestions = result[3].data;
                }

                this.actions = result[4].data;

                if (!this.reload) {
                    if (this.enableDeepLinking) {
                        this.handleDeepLinking();
                    }else{
                        this.router.navigate([
                            '/client', this.client.code, 
                            'account', this.account.code, this.account.versionNumber                            
                        ], { replaceUrl: true });
                        this.selectedTabIndex = 1;
                        this.tabGroup.selectedIndex = 1;
                    }
                }

                this.loadingData = false;
            });
    }
    
    private handleDeepLinking() {
        // Check if we need to activate Settings tab for deep linking
        if (this.route.snapshot.data['activeTab'] === 'Settings') {
            setTimeout(() => {
                this.activateSettingsTab();
                // Clear deep linking flags after Settings activation
                setTimeout(() => {
                    this.clearDeepLinkingFlags();
                }, 200);
            }, 100);
        }
        // Check if we need to activate a main tab for deep linking
        else if (this.targetTabName) {
            const targetIndex = this.getTabIndexByName(this.targetTabName);
            
            if (targetIndex >= 0) {
                // Force the tab selection immediately
                this.selectedTabIndex = targetIndex;
                
                // Activate the tab in the component state
                const tabLabel = this.getTabLabelByName(this.targetTabName);
                this.activatedTabs[tabLabel] = true;
                
                // Use a longer timeout to ensure the view is ready
                setTimeout(() => {
                    if (this.tabGroup) {
                        this.tabGroup.selectedIndex = targetIndex;
                    }
                    // Clear deep linking flags after main tab activation
                    setTimeout(() => {
                        this.clearDeepLinkingFlags();
                    }, 200);
                }, 300);
            } else {
                // Fall back to first tab and clear flags
                this.selectedTabIndex = 0;
                this.clearDeepLinkingFlags();
            }
        }
        // Default tab selection
        else {
            this.selectedTabIndex = 1;
            setTimeout(() => {
                if (this.tabGroup) {
                    this.tabGroup.selectedIndex = 1;
                }
            }, 100);
            // Clear flags since no deep linking occurred
            this.clearDeepLinkingFlags();
        }
    }
    
    private clearDeepLinkingFlags() {
        if (!this.enableDeepLinking) return;
        this.targetSettingType = undefined;
        this.targetTabName = undefined;
    }


    flushCache() {
        const url = `System/FlushCache`;

        forkJoin([
            this.appService.addData(url, null),
            this.globalService.flushPublicApiCache()
        ]).subscribe(result => {
            const internalStatus = result[0].status;
            const publicApiStatus = result[1];

            if (internalStatus === 'success' && publicApiStatus) {
                this.reload = true;
                this.getAccountData();
            }
        });
    }

    tabChange(event) {        
        if (event.tab.textLabel !== 'Billing') {
            // We need to reload the billing tab control between tab views
            this.activatedTabs['Billing'] = false;
        }
        if (event.tab.textLabel === 'MapKeys' && this.activatedTabs[event.tab.textLabel] != null && this.accountMapKeysComponent) {
            this.accountMapKeysComponent.refreshView();
        }

        if (!this.activatedTabs[event.tab.textLabel]) {
            this.activatedTabs[event.tab.textLabel] = true;
        }
        if (event.tab.textLabel === 'Application') {
            if (this.accountInterviewComponent) {
                this.accountInterviewComponent.scrollToQuestionOrSectionInNavigation();
            }
        }
        
        // Save the current tab index for state restoration
        this.selectedTabIndex = event.index;
        this.saveCurrentState();
        
        // Force clear deep linking flags on user interaction to ensure URL updates work
        if (this.targetSettingType || this.targetTabName) {
            this.clearDeepLinkingFlags();
        }
        
        // Update URL for all tab changes
        this.updateUrlForTab(event.tab.textLabel);        
    }

    updateAccountInfo(account: Account) {
        if (this.isNewAccount) {
            // Navigate to the new URL so we reload everything properly
            this.routingService.navigateToRoute(RoutesEnum.accountDetails, {
                clientCode: this.client.code,
                accountCode: account.code,
                versionNumber: account.versionNumber
            });
        } else {
            Object.assign(this.account, account);

            this.getMapKeys(true);
            this.getRules();
        }
    }

    checkACL(feature, permissionType, redirect?) {
        return this.appService.checkACL(feature, permissionType, redirect);
    }

    back() {
        this.routingService.navigateToRoute(RoutesEnum.clients);
    }

    isActive(selected) {
        return this.activeMenu === selected;
    }

    addClient() {
        this.newClientForm.reset();

        this.newClientForm.patchValue({
            code: 'AA',
            status: '00000000-0000-0000-0000-000000000000',
            name: '',
        });
        this.companyLogo = this.appService.getDefaultLogo();

        this.currentClientID = undefined;
        this.newClient = true;
    }

    getMapKeys(reloadTree) {
        if (!this.isNewAccount) {
            this.mapKeyService.getMapKeys(this.account.id, reloadTree).subscribe(() => {
                this.setMapKeyData();
            });
        }
    }

    setMapKeyData() {
        this.mapKeys = this.mapKeyService.mapKeys;
        this.constantListMapKeys = this.mapKeyService.constantListMapKeys;
        this.nonConstantMapKeys = this.mapKeyService.nonConstantMapKeys;
        this.nonVirtualMapKeys = this.mapKeyService.nonVirtualMapKeys;
        this.nonListNonVirtualMapKeys = this.mapKeyService.nonListNonVirtualMapKeys;
        this.nonListMapKeys = this.mapKeyService.nonListMapKeys;
        this.variableMapKeys = this.mapKeyService.variableMapKeys;
    }

    getRules() {
        this.appService.getData<UWResponse<RulesDTO[]>>(`Rules/${this.account.id}`).subscribe(data => {
            if (data.status === 'success') {
                this.rules = data.data;
                this.mapKeyService.rules = this.rules;
                if (this.accountMapKeys) {
                    this.accountMapKeys.rules = this.rules;
                }
            }
        });
    }

    startInterview() {
        this.routingService.navigateToRouteInNewTab(RoutesEnum.interview, {
            clientCode: this.client.code,
            accountCode: this.account.code,
            versionNumber: this.account.versionNumber
        });
    }

    getSecretKeys() {
        this.accountDataService.getSecretKeys().subscribe(keys => this.secretKeys = keys);      
    }

    ngOnDestroy() {
        this.mapKeyService.clearData();
    }

}
